import ItemModal from "@/components/ItemModal";
import type {
  ItemType,
  IngredientType,
  ItemWithIngredientsType,
  IngredientGroupType,
} from "@/types/item";
import { createContext, useContext, useEffect, useState } from "react";
import { useSearch } from "./SearchProvider";

interface ItemModalConfig {
  item: ItemType;
  ctaText: string;
  onCtaClick: (ingredients: IngredientType[], quantity: number) => void;
  showQuantitySelector?: boolean;
  ingredientGroups?: IngredientGroupType[];
}

interface ItemModalContextType {
  isOpen: boolean;
  open: (config: ItemModalConfig) => void;
  close: () => void;
}

const ItemModalContext = createContext<ItemModalContextType | undefined>(
  undefined
);

export const ItemModalProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<ItemModalConfig | null>(null);
  const [selectedIngredients, setSelectedIngredients] = useState<
    IngredientType[]
  >([]);
  const [quantity, setQuantity] = useState(1);

  const { deactivateSearch } = useSearch();

  useEffect(() => {
    if (config?.ingredientGroups) {
      setSelectedIngredients(
        config.ingredientGroups.flatMap((group) =>
          group.ingredients.filter((ing) => ing.isDefault)
        )
      );
    }
  }, [config]);

  const selectIngredient = (ingredient: IngredientType) => {
    const itemWithIngredients = config?.item as ItemWithIngredientsType;

    const ingredientGroup = itemWithIngredients.ingredientGroups.find((group) =>
      group.ingredients.some((ing) => ing.id === ingredient.id)
    );

    // if the ingredient group is single option
    if (!ingredientGroup?.isMulti) {
      // remove all ingredients of the group from selectedIngredients and add the new one
      setSelectedIngredients((prevSelected) => {
        const filteredIngredients = prevSelected.filter(
          (sing) =>
            !ingredientGroup?.ingredients.some((ing) => sing.id === ing.id)
        );
        return [...filteredIngredients, ingredient];
      });
    } else {
      // for multi-select, just add the ingredient
      setSelectedIngredients((prevSelected) => [...prevSelected, ingredient]);
    }
  };

  const unselectIngredient = (ingredient: IngredientType) => {
    setSelectedIngredients(
      selectedIngredients.filter((ing) => ing.id !== ingredient.id)
    );
  };

  const open = (config: ItemModalConfig) => {
    setQuantity(1);
    setSelectedIngredients([]);
    setConfig(config);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  return (
    <ItemModalContext.Provider
      value={{
        isOpen,
        open,
        close,
      }}
    >
      {children}
      {config && (
        <ItemModal
          open={isOpen}
          onClose={() => {
            close();
          }}
          ctaText={config.ctaText}
          selectIngredient={selectIngredient}
          unselectIngredient={unselectIngredient}
          onCtaClick={() => {
            close();
            deactivateSearch();
            config.onCtaClick(selectedIngredients, quantity);
          }}
          item={config.item}
          ingredientGroups={config.ingredientGroups}
          selectedIngredients={selectedIngredients}
          quantity={config.showQuantitySelector ? quantity : undefined}
          onQuantityChange={
            config.showQuantitySelector ? setQuantity : undefined
          }
        />
      )}
    </ItemModalContext.Provider>
  );
};

export const useItemModal = () => {
  const context = useContext(ItemModalContext);
  if (context === undefined) {
    throw new Error("useItemModal must be used within a ItemModalProvider");
  }
  return context;
};
